import { useEffect, useRef, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, Grid3x3, ClipboardList, Bike, Ticket, Car, QrCode, Package, Users, BarChart3,
  Settings, Crown, LogOut, CakeSlice, Moon, Sun, Menu, Wallet, ChefHat, Boxes, ShieldOff, Building2, UserCog,
  ChevronDown, Cog, Receipt, Monitor, ListChecks, Star, Smartphone, Gift, Printer, Zap, Route, Settings2, Share2, FileText, Banknote, Clock, Briefcase,
} from "lucide-react";
import { CentralPrinter } from "@/components/CentralPrinter";
import { OrderAlarm } from "@/components/OrderAlarm";
import { WaAlerts } from "@/components/WaAlerts";
import { HelpAssistant } from "@/components/help/HelpAssistant";
import { GuidedTour } from "@/components/tour/GuidedTour";
import { NavSearch } from "@/components/NavSearch";
import { KW, SEARCH_EXTRA } from "@/lib/searchIndex";
import { useFavorites } from "@/lib/useFavorites";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth, useTheme } from "@/context/AuthContext";
import { TrialBanner, BlockWarningBanner } from "@/components/TrialBanner";
import { StoreSwitcher } from "@/components/StoreSwitcher";
import { Announcements } from "@/components/Announcements";
import { PlanStatusBadge, EmptyState } from "@/components/common";
import { CompanyCard, CompanyCompact } from "@/components/CompanyCard";
import { SupportButton } from "@/components/SupportDialog";
import { IconAction } from "@/components/IconAction";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ROLE_LABELS, canSee, hexToHsl, isAdminRole, isDelegated, isManagerRole } from "@/lib/api";
import { cn } from "@/lib/utils";

export const NAV = [
  { to: "/app", label: "Painel", icon: LayoutDashboard, end: true, key: "painel" },
  { to: "/app/comandas", label: "Comanda", icon: ClipboardList, key: "comandas", group: "pdv" },
  { to: "/app/pdv", label: "PDV / Caixa", icon: ShoppingCart, key: "pdv", group: "pdv" },
  { to: "/app/mesas", label: "Mesa", icon: Grid3x3, key: "mesas", group: "pdv" },
  { to: "/app/venda-ticket", label: "Venda ticket", icon: Receipt, key: "venda_ticket", group: "pdv" },
  { to: "/app/consulta-itens", label: "Consulta itens venda", icon: ListChecks, key: "consulta_itens", group: "pdv" },
  { to: "/app/acesso-movel", label: "Link do app (celular)", icon: Smartphone, key: "comandas", tid: "acesso-movel", group: "pdv", mgr: true },
  { to: "/app/caixa", label: "Controle de caixa", icon: Wallet, key: "caixa" },
  { to: "/app/encomendas", label: "Encomendas", icon: CakeSlice, key: "encomendas" },
  { to: "/app/delivery", label: "Delivery", icon: Bike, key: "delivery" },
  { to: "/app/boy-express", label: "Corridas e cadastros", icon: Route, key: "boy_express", group: "boy", end: true },
  { to: "/app/boy-express/config", label: "Configurações", icon: Settings2, key: "boy_express", tid: "boy-config", group: "boy", mgr: true },
  { to: "/app/fidelidade", label: "Fidelidade", icon: Gift, key: "fidelidade" },
  { to: "/app/avaliacoes", label: "Avaliações", icon: Star, key: "delivery", tid: "avaliacoes" },
  { to: "/app/impressoes", label: "Impressões", icon: Printer, key: "pdv", keys: ["pdv", "delivery"], tid: "impressoes" },
  { to: "/app/producao", label: "Produção", icon: ChefHat, key: "producao" },
  { to: "/app/tickets", label: "Tickets", icon: Ticket, key: "tickets" },
  { to: "/app/estacionamento", label: "Estacionamento", icon: Car, key: "estacionamento" },
  { to: "/app/cardapio", label: "Cardápio digital", icon: QrCode, key: "cardapio" },
  { to: "/app/produtos", label: "Produtos", icon: Package, key: "produtos" },
  { to: "/app/estoque", label: "Estoque", icon: Boxes, key: "estoque" },
  { to: "/app/clientes", label: "Clientes", icon: Users, key: "clientes" },
  { to: "/app/relatorios", label: "Relatórios", icon: BarChart3, key: "relatorios" },
  { to: "/app/fiscal", label: "Fiscal", icon: FileText, key: "fiscal" },
  { to: "/app/folha", label: "Folha de pagamento", icon: Banknote, key: "folha" },
  { to: "/app/ponto", label: "Ponto e escala", icon: Clock, key: "ponto" },
  { to: "/app/contabil", label: "Meus clientes", icon: Briefcase, key: "contabil" },
  { to: "/app/planos", label: "Planos", icon: Crown, key: "planos" },
];

export const SYSTEM_NAV = [
  { to: "/app/sistema/empresa", label: "Minha empresa", icon: Building2, key: "sistema_empresa", admin: true },
  { to: "/app/sistema/usuarios", label: "Usuários", icon: UserCog, key: "sistema_usuarios", admin: true },
  { to: "/app/sistema/contador", label: "Meu contador", icon: Briefcase, key: "sistema_contador", admin: true },
  { to: "/app/indicacoes", label: "Indicações", icon: Share2, key: "indicacoes", admin: true },
  { to: "/app/configuracoes", label: "Configurações", icon: Settings, key: "configuracoes" },
];
const GROUPS = { pdv: { label: "PDV", icon: Monitor, testId: "pdv" }, boy: { label: "Boy Express", icon: Zap, testId: "boy-express" } };
const visible = (user, n) => (n.admin ? isAdminRole(user) : (n.keys || [n.key]).some((k) => canSee(user, k))) && (!n.mgr || isManagerRole(user));
const searchItems = (user) => [...NAV.map((n) => ({ ...n, parent: GROUPS[n.group]?.label || "" })), ...SYSTEM_NAV.map((n) => ({ ...n, parent: "Sistema" })), ...SEARCH_EXTRA]
  .filter((n) => visible(user, n)).map((n) => ({ ...n, kw: n.kw || KW[n.to] || "" }));
const areaOf = (path) => [...NAV, ...SYSTEM_NAV].sort((a, b) => b.to.length - a.to.length).find((n) => (n.end ? path === n.to || path === `${n.to}/` : path.startsWith(n.to)));

function FavStar({ fav, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} title={fav ? "Remover dos favoritos" : "Fixar nos favoritos"} data-testid={testId}
      className={cn("absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 transition-opacity hover:text-amber-400 focus-visible:opacity-100",
        fav ? "text-amber-400" : "text-muted-foreground md:bg-card md:opacity-0 md:group-hover:opacity-100")}>
      <Star className={cn("h-3.5 w-3.5", fav && "fill-amber-400")} />
    </button>
  );
}

function NavLinkItem({ to, label, icon: Icon, end, navKey, onNavigate, nested, fav, onFav }) {
  return (
    <div className="group relative">
      <NavLink to={to} end={end} onClick={onNavigate} data-testid={`nav-${navKey}`}
        className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 pr-9 text-sm font-medium transition-colors touch", !fav && "md:pr-3", nested && "pl-8",
          isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
        <Icon className="h-4 w-4" />{label}
      </NavLink>
      {onFav && <FavStar fav={fav} onClick={() => onFav(to)} testId={`nav-fav-${navKey}`} />}
    </div>
  );
}

function NavFavorites({ user, favs, onFav, onNavigate }) {
  const all = [...NAV, ...SYSTEM_NAV].filter((n) => visible(user, n));
  const pinned = favs.map((to) => all.find((n) => n.to === to)).filter(Boolean);
  if (!pinned.length) return null;
  return (
    <div className="mb-2 border-b pb-2" data-testid="nav-favorites">
      <p className="flex items-center gap-1.5 px-3 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />Favoritos</p>
      {pinned.map(({ key, group, ...n }) => <NavLinkItem key={n.to} {...n} navKey={`favorito-${n.tid || key}`} onNavigate={onNavigate} fav onFav={onFav} />)}
    </div>
  );
}

function NavGroup({ label, icon: Icon, items, testId, onNavigate, border, favs, onFav, collapsed }) {
  const { pathname } = useLocation();
  const active = items.some((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
  const [open, setOpen] = useState(!collapsed || active);
  const ref = useRef(null);
  const reveal = useRef(false);
  useEffect(() => {
    if (open && reveal.current) ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    reveal.current = false;
  }, [open]);
  if (!items.length) return null;
  return (
    <div ref={ref} className={cn(border && "mt-2 border-t pt-2")} data-testid={`nav-group-${testId}`}>
      <button onClick={() => { reveal.current = !open; setOpen(!open); }} className={cn("flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors hover:bg-accent", active ? "text-primary" : "text-foreground")} data-testid={`nav-${testId}-toggle`}>
        <Icon className="h-4 w-4" />{label}<ChevronDown className={cn("ml-auto h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="flex flex-col gap-1 mt-1">{items.map(({ key, group, ...n }) => <NavLinkItem key={n.to} {...n} navKey={n.tid || key} onNavigate={onNavigate} nested fav={favs.includes(n.to)} onFav={onFav} />)}</div>}
    </div>
  );
}

function NavItems({ onNavigate, user, hotkey }) {
  const list = NAV.filter((n) => visible(user, n));
  const [favs, onFav] = useFavorites();
  const fp = { favs, onFav };
  return (
    <NavSearch items={searchItems(user)} onNavigate={onNavigate} hotkey={hotkey}>
    <NavFavorites user={user} favs={favs} onFav={onFav} onNavigate={onNavigate} />
    <nav className="flex flex-col gap-1">
      {list.map(({ key, group, ...n }, i) => (group
        ? list.findIndex((x) => x.group === group) === i && <NavGroup key={`g-${group}`} label={GROUPS[group].label} icon={GROUPS[group].icon} items={list.filter((x) => x.group === group)} testId={GROUPS[group].testId} onNavigate={onNavigate} collapsed {...fp} />
        : <NavLinkItem key={n.to} {...n} navKey={n.tid || key} onNavigate={onNavigate} fav={favs.includes(n.to)} onFav={onFav} />))}
      <NavGroup label="Sistema" icon={Cog} items={SYSTEM_NAV.filter((n) => visible(user, n))} testId="sistema" onNavigate={onNavigate} border collapsed {...fp} />
    </nav>
    </NavSearch>
  );
}

function UserInfo({ session }) {
  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold" data-testid="current-user-name">{session.user.name}</p>
          <p className="text-xs text-muted-foreground">{ROLE_LABELS[session.user.role]}</p>
        </div>
        <PlanStatusBadge status={session.subscription.status} testId="sidebar-plan-status" />
      </div>
    </div>
  );
}

function SideActions({ session, onLogout, theme, toggleTheme }) {
  return (
    <TooltipProvider delayDuration={150}>
      <div className="mt-3 flex shrink-0 gap-2 border-b pb-3" data-testid="sidebar-actions">
        <IconAction label={theme === "dark" ? "Tema claro" : "Tema escuro"} icon={theme === "dark" ? Sun : Moon} onClick={toggleTheme} testId="theme-toggle-btn" />
        {canSee(session.user, "comandas") && <IconAction label="App de comandas" icon={Smartphone} to="/m" testId="open-mobile-app-btn" />}
        <SupportButton compact />
        <IconAction label="Sair" icon={LogOut} onClick={onLogout} testId="logout-btn" danger />
      </div>
    </TooltipProvider>
  );
}

function useBrandColor(color) {
  useEffect(() => {
    const c = hexToHsl(color);
    const root = document.documentElement.style;
    if (!c) return undefined;
    root.setProperty("--primary", c.hsl);
    root.setProperty("--ring", c.hsl);
    root.setProperty("--primary-foreground", c.light ? "222 47% 11%" : "0 0% 100%");
    return () => ["--primary", "--ring", "--primary-foreground"].forEach((k) => root.removeProperty(k));
  }, [color]);
}

function ContadorBanner({ session, onSair }) {
  const c = session.user.contador;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-amber-500/15 px-4 py-2 text-sm" data-testid="contador-banner">
      <Briefcase className="h-4 w-4 text-amber-600" />
      <span className="flex-1">Você está no sistema de <b>{session.tenant.nome_fantasia || session.tenant.razao_social}</b> como contador ({c.escritorio}). {c.operar.length ? "Pode operar: " + c.operar.join(", ") + "." : "Somente consulta."} Tudo fica registrado para o cliente.</span>
      <Button size="sm" variant="outline" onClick={onSair}>Sair do cliente</Button>
    </div>
  );
}

export default function AppLayout() {
  const { session, logout } = useAuth();
  const [theme, toggleTheme] = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const user = session.user;
  useBrandColor(session.tenant.settings?.brand_color);
  const doLogout = async () => {
    const dlg = isDelegated();
    await logout();
    if (dlg) window.location.href = "/app/contabil"; // contador volta ao painel do escritório
    else navigate("/login");
  };
  const allowed = NAV.filter((n) => visible(user, n));
  const preferred = allowed.filter((n) => !n.tid && ["painel", "pdv", "venda_ticket", "mesas", "delivery"].includes(n.key));
  const mobile = (preferred.length >= 2 ? preferred : allowed).slice(0, 4);
  const item = areaOf(pathname);
  if (item?.key === "painel" && session.tenant.segmento === "contabilidade" && !user.contador && canSee(user, "contabil")) return <Navigate to="/app/contabil" replace />;
  if (item?.key === "painel" && !canSee(user, "painel") && allowed[0]) return <Navigate to={`${allowed[0].to}${search}`} replace />;
  const blocked = item && !visible(user, item);
  const actions = { session, onLogout: doLogout, theme, toggleTheme };

  return (
    <div className="min-h-screen bg-background">
      {!user.contador && <><CentralPrinter session={session} />
      <OrderAlarm />
      <WaAlerts /></>}
      <HelpAssistant />
      <GuidedTour />
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-card p-4 md:flex">
        <CompanyCard tenant={session.tenant} user={user} />
        <SideActions {...actions} />
        <StoreSwitcher />
        <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1" data-testid="sidebar-scroll">
          <NavItems user={user} hotkey />
        </div>
        <UserInfo session={session} />
      </aside>

      <div className="md:pl-64 overflow-x-clip">
        <header className="sticky top-0 z-20 backdrop-blur-md bg-background/80">
          <div className="flex items-center justify-between border-b px-4 py-3 md:hidden">
            <CompanyCompact tenant={session.tenant} user={user} />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="mobile-theme-toggle-btn">
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>
          {user.contador ? <ContadorBanner session={session} onSair={doLogout} /> : <><TrialBanner />
          <BlockWarningBanner /></>}
        </header>
        <main className="mx-auto max-w-7xl space-y-6 p-4 pb-24 sm:p-6 lg:p-8 md:pb-8">
          {blocked ? <EmptyState icon={ShieldOff} title="Sem acesso a esta área" text="Seu nível de acesso ou o seu plano não permite abrir esta tela. Fale com o administrador ou veja os planos." /> : <>{!user.contador && <Announcements />}<Outlet /></>}
        </main>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-40 flex h-16 items-center justify-around border-t bg-card md:hidden" data-testid="mobile-bottom-nav">
        {mobile.map(({ to, label, icon: Icon, end, key }) => (
          <NavLink key={to} to={to} end={end} data-testid={`mobile-nav-${key}`}
            className={({ isActive }) => cn("flex flex-col items-center gap-1 px-2 text-[11px]", isActive ? "text-primary" : "text-muted-foreground")}>
            <Icon className="h-5 w-5" />
            {label.split(" ")[0]}
          </NavLink>
        ))}
        <button onClick={() => setOpen(true)} className="flex flex-col items-center gap-1 px-2 text-[11px] text-muted-foreground" data-testid="mobile-nav-more">
          <Menu className="h-5 w-5" /> Mais
        </button>
      </nav>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 flex flex-col">
          <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
          <SideActions {...actions} />
          <StoreSwitcher />
          <div className="min-h-0 flex-1 overflow-y-auto"><NavItems user={user} onNavigate={() => setOpen(false)} /></div>
          <UserInfo session={session} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
